import React, { useCallback, useEffect, useRef, useState } from "react";
import { Modal } from "antd";
import { PictureOutlined } from "@ant-design/icons";
import ReactCrop from "react-image-crop";
import "react-image-crop/src/ReactCrop.scss";
import { openNotificationWithIcon } from "utils/Notification";
import { Button } from "components/Buttons/Buttons";

const pixelRatio = window.devicePixelRatio || 1;

const PREVIEW_WIDTH = 220;
const PREVIEW_HEIGHT = 140;

export default function DealImageUpload({ base64, setBase64, setFile, isEdit }) {
    const [modalVisible, setModalVisible] = useState(false);
    const [upImg, setUpImg] = useState(null);
    const imgRef = useRef(null);
    const previewCanvasRef = useRef(null);
    const [crop, setCrop] = useState({ unit: "%", width: 80, height: 60, x: 10, y: 10 });
    const [completedCrop, setCompletedCrop] = useState(null);
    const [finalCrop, setFinalCrop] = useState("");

    const onSelectFile = (e) => {
        if (e.target.files && e.target.files.length > 0) {
            setFile && setFile(e.target.files[0]);
            const reader = new FileReader();
            reader.addEventListener("load", () => setUpImg(reader.result));
            reader.readAsDataURL(e.target.files[0]);
            e.target.value = "";
        }
    };

    const onLoad = useCallback((img) => {
        imgRef.current = img;
    }, []);

    useEffect(() => {
        if (!completedCrop || !previewCanvasRef.current || !imgRef.current) {
            return;
        }

        const image = imgRef.current;
        const canvas = previewCanvasRef.current;
        const crop = completedCrop;

        const scaleX = image.naturalWidth / image.width;
        const scaleY = image.naturalHeight / image.height;
        const ctx = canvas.getContext("2d");

        canvas.width = crop.width * pixelRatio;
        canvas.height = crop.height * pixelRatio;

        ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
        ctx.imageSmoothingQuality = "high";

        ctx.drawImage(
            image,
            crop.x * scaleX,
            crop.y * scaleY,
            crop.width * scaleX,
            crop.height * scaleY,
            0,
            0,
            crop.width,
            crop.height
        );
        setFinalCrop(canvas.toDataURL());
    }, [completedCrop]);

    return (
        <div>
            <Modal
                visible={modalVisible}
                title={`${isEdit ? "Update" : "Upload"} Deal Image`}
                maskClosable={false}
                onCancel={() => {
                    setModalVisible(false);
                    setUpImg("");
                    setFinalCrop("");
                }}
                onOk={() => {
                    if (finalCrop) {
                        if (finalCrop.includes("base64")) {
                            setBase64(finalCrop);
                            setModalVisible(false);
                            setUpImg("");
                        } else {
                            return openNotificationWithIcon(
                                "warning",
                                "Warning!",
                                "Please crop picture area"
                            );
                        }
                    } else {
                        openNotificationWithIcon(
                            "warning",
                            "Warning!",
                            "Please upload a valid file"
                        );
                    }
                }}
            >
                <ReactCrop
                    src={upImg}
                    onImageLoaded={onLoad}
                    crop={crop}
                    keepSelection={false}
                    onChange={(c) => setCrop(c)}
                    onComplete={(c) => setCompletedCrop(c)}
                />
                {!upImg ? (
                    <div
                        style={{
                            padding: "20px 0",
                            borderTop: "2px dashed #D8DCE4",
                            borderBottom: "2px dashed #D8DCE4",
                            textAlign: "center",
                        }}
                    >
                        <label style={{ display: "inline-block", cursor: "pointer" }}>
                            <input
                                type="file"
                                accept="image/*"
                                onChange={onSelectFile}
                                style={{ display: "none" }}
                            />
                            <span
                                style={{
                                    display: "inline-block",
                                    background: "#486baf",
                                    borderRadius: 5,
                                    color: "#fff",
                                    fontSize: 16,
                                    padding: "10px 15px",
                                }}
                            >
                                Browse for File
                            </span>
                        </label>
                    </div>
                ) : (
                    <p>Drag the corners to crop the image to the area you want.</p>
                )}
                <canvas
                    ref={previewCanvasRef}
                    style={{
                        width: Math.round(completedCrop?.width ?? 0),
                        height: Math.round(completedCrop?.height ?? 0),
                        display: "none",
                    }}
                />
            </Modal>
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexDirection: "column",
                }}
            >
                {base64 ? (
                    <img
                        src={base64}
                        width={PREVIEW_WIDTH}
                        height={PREVIEW_HEIGHT}
                        style={{ borderRadius: 8, objectFit: "cover" }}
                        alt=""
                    />
                ) : (
                    <div
                        style={{
                            width: PREVIEW_WIDTH,
                            height: PREVIEW_HEIGHT,
                            borderRadius: 8,
                            border: "1px dashed #C4CDD5",
                            background: "#F7F8FA",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                    >
                        <PictureOutlined style={{ fontSize: 32, color: "#C4CDD5" }} />
                    </div>
                )}
                <Button
                    className="uploadImageBtn"
                    style={{ marginTop: 12 }}
                    onClick={() => {
                        setCrop({ unit: "%", width: 80, height: 60, x: 10, y: 10 });
                        setModalVisible(true);
                    }}
                >
                    {isEdit ? "Update" : "Upload"} Image
                </Button>
            </div>
        </div>
    );
}
